import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';
import { loggingService } from '@/services/loggingService';
import { Log, LogLevel, LogCategory } from '@/types/logs';
import { useAuth } from './AuthContext'; // Correctly import useAuth

interface LogContextType {
  logs: Log[];
  addLog: (message: string, level: LogLevel, category: LogCategory, data?: Record<string, unknown>) => void;
  isLoading: boolean;
  error: string | null;
}

const LogContext = createContext<LogContextType | undefined>(undefined);

export const LogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [logs, setLogs] = useState<Log[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error] = useState<string | null>(null);
  const { currentUser } = useAuth(); // Get the current user from AuthProvider

  const addLog = useCallback((message: string, level: LogLevel, category: LogCategory, data?: Record<string, unknown>) => {
    const newLog: Log = {
      id: Date.now().toString(),
      timestamp: Date.now(),
      message,
      level,
      category,
      userId: currentUser?.id, // <-- The critical fix: add the user ID to the log
      data,
    };
    setLogs(prevLogs => [...prevLogs, newLog]);
    // For immediate feedback and debugging in the console
    console.log(`[${level.toUpperCase()}] [${category.toUpperCase()}] ${message}`, { ...data, userId: currentUser?.id });
  }, [currentUser]); // Add currentUser as a dependency

  useEffect(() => {
    // This allows other parts of the app to use the logging service without a direct dependency on the context
    loggingService.setAddLogCallback(addLog);
  }, [addLog]);

  // This effect can be used to fetch initial logs from a backend if needed
  useEffect(() => {
    setIsLoading(true);
    // Example: fetch('/api/logs').then(...)
    setIsLoading(false);
  }, []);

  return (
    <LogContext.Provider value={{ logs, addLog, isLoading, error }}>
      {children}
    </LogContext.Provider>
  );
};

// Create a fallback logger for when the context is not available
const fallbackLogger: LogContextType = {
  logs: [],
  isLoading: false,
  error: 'Logger not initialized',
  addLog: (message: string, level: LogLevel, category: LogCategory, data?: Record<string, unknown>) => {
    console.warn(
      `[LOGGER-FALLBACK] A log was triggered before the LogProvider was ready. ` +
      `This may be due to a circular dependency or incorrect component nesting. ` +
      `The log will be sent to the console instead of the main log state. `,
      { level, category, message, data }
    );
  },
};

export const useLogger = (): LogContextType => {
  const context = useContext(LogContext);
  if (context === undefined) {
    // Instead of throwing an error, return the fallback implementation.
    // This prevents the app from crashing if a log is fired during startup
    // from a component that is not a descendant of the LogProvider (e.g., AuthProvider).
    return fallbackLogger;
  }
  return context;
};
